import React, { useState, useEffect } from 'react'
import { ChevronDown, Flame, Clock, Shield, Award, X, Zap } from 'lucide-react'

const PILLARS = [
  {
    icon: Flame,
    label: 'Engagement',
    color: 'from-orange-500 to-red-500',
    glow: 'bg-orange-500',
    border: 'border-orange-500/20',
    description: 'How much people are talking about it',
    details: [
      'Reddit upvotes & comments',
      'X / Twitter engagement',
      'Logarithmic scale so one viral post doesn\'t dominate',
    ],
  },
  {
    icon: Clock,
    label: 'Freshness',
    color: 'from-cyan-400 to-blue-500',
    glow: 'bg-cyan-500',
    border: 'border-cyan-500/20',
    description: 'How recently the story broke',
    details: [
      'Power-law decay inspired by Hacker News',
      'Breaking stories surge to the top',
      'Older stories gracefully fade',
    ],
  },
  {
    icon: Shield,
    label: 'Verification',
    color: 'from-emerald-400 to-green-500',
    glow: 'bg-emerald-500',
    border: 'border-emerald-500/20',
    description: 'How many sources are covering it',
    details: [
      '5+ outlets = 2x boost (major breaking news)',
      '3+ outlets = 1.5x (significant story)',
      'Cross-checked against Google News, AP & Reddit',
    ],
  },
  {
    icon: Award,
    label: 'Authority',
    color: 'from-violet-400 to-purple-500',
    glow: 'bg-violet-500',
    border: 'border-violet-500/20',
    description: 'How credible the sources are',
    details: [
      'Reuters, AP, BBC, NYT = highest tier',
      'TechCrunch, Bloomberg, Axios = strong tier',
      'Unknown blogs are weighted lower',
    ],
  },
]

function PillarCard({ pillar, isExpanded }) {
  const Icon = pillar.icon
  return (
    <div className={`relative group rounded-2xl border ${pillar.border} bg-white/[0.02] active:bg-white/[0.06] md:hover:bg-white/[0.04] transition-all duration-300 ${isExpanded ? 'p-4 md:p-5' : 'p-3.5 md:p-4'}`}>
      <div className={`absolute -inset-px rounded-2xl ${pillar.glow} opacity-0 group-hover:opacity-[0.06] blur-xl transition-opacity duration-500 hidden md:block`} />
      <div className="relative">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-white tracking-tight">{pillar.label}</h4>
            <p className="text-[11px] text-gray-500 leading-snug">{pillar.description}</p>
          </div>
        </div>
        {isExpanded && (
          <ul className="space-y-2 md:space-y-1.5 mt-3 pt-3 border-t border-white/5">
            {pillar.details.map((detail, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] md:text-[11px] text-gray-400 leading-relaxed">
                <span className={`mt-1.5 w-1 h-1 rounded-full bg-gradient-to-r ${pillar.color} flex-shrink-0`} />
                {detail}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function HeatBarDemo() {
  const examples = [
    { label: 'Viral + fresh + verified', pct: 95, score: '869' },
    { label: 'High engagement, aging', pct: 62, score: '545' },
    { label: 'Moderate, single source', pct: 30, score: '265' },
  ]

  return (
    <div className="space-y-3">
      <h4 className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase">What the bar means</h4>
      {examples.map((ex, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-24 md:w-28 h-2 bg-gray-800/80 rounded-full overflow-hidden flex-shrink-0">
            <div
              className={`h-full rounded-full ${
                ex.pct > 60 ? 'bg-gradient-to-r from-orange-500 via-red-500 to-rose-500'
                : ex.pct > 30 ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                : 'bg-gradient-to-r from-blue-400 to-cyan-500'
              }`}
              style={{ width: `${ex.pct}%` }}
            />
          </div>
          <span className="text-[12px] md:text-[11px] font-mono text-gray-400 w-8 tabular-nums">{ex.score}</span>
          <span className="text-[12px] md:text-[11px] text-gray-500">{ex.label}</span>
        </div>
      ))}
    </div>
  )
}

function ExplainerContent({ showDetails, setShowDetails, onClose }) {
  return (
    <>
      <div className="relative px-5 md:px-6 pt-5 md:pt-6 pb-3 md:pb-4">
        <div className="absolute top-0 right-0 w-48 md:w-64 h-48 md:h-64 bg-gradient-to-bl from-amber-500/[0.04] to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-0 left-0 w-36 md:w-48 h-36 md:h-48 bg-gradient-to-br from-cyan-500/[0.03] to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base md:text-lg font-bold text-white tracking-tight mb-1">
              How stories are ranked
            </h3>
            <p className="text-[13px] md:text-xs text-gray-400 leading-relaxed">
              Every story gets a heat score from four signals. No editors pick favorites
              &mdash; it's pure math combining what's trending, what's new, and what's trustworthy.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl active:bg-white/10 md:hover:bg-white/10 text-gray-500 active:text-white md:hover:text-white transition-colors touch-target flex-shrink-0"
          >
            <X className="w-5 h-5 md:w-4 md:h-4" />
          </button>
        </div>

        <div className="mt-4 md:mt-5 flex flex-wrap items-center justify-center gap-1.5 md:gap-2 py-3 px-3 md:px-4 rounded-xl bg-black/40 border border-white/5">
          <span className="text-[12px] md:text-[11px] font-mono text-gray-500">heat</span>
          <span className="text-[12px] md:text-[11px] font-mono text-gray-600">=</span>
          <span className="text-[12px] md:text-[11px] font-mono text-orange-400">engagement</span>
          <span className="text-[12px] md:text-[11px] font-mono text-gray-600">&times;</span>
          <span className="text-[12px] md:text-[11px] font-mono text-cyan-400">freshness</span>
          <span className="text-[12px] md:text-[11px] font-mono text-gray-600">&times;</span>
          <span className="text-[12px] md:text-[11px] font-mono text-emerald-400">verification</span>
          <span className="text-[12px] md:text-[11px] font-mono text-gray-600">&times;</span>
          <span className="text-[12px] md:text-[11px] font-mono text-violet-400">authority</span>
        </div>
      </div>

      <div className="px-5 md:px-6 pb-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-3">
          {PILLARS.map((pillar, i) => (
            <PillarCard key={pillar.label} pillar={pillar} isExpanded={showDetails} />
          ))}
        </div>
      </div>

      <div className="px-5 md:px-6 py-3">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-[11px] md:text-[10px] font-semibold tracking-[0.1em] text-gray-500 active:text-white md:hover:text-white transition-colors flex items-center gap-1 touch-target"
        >
          {showDetails ? 'LESS DETAIL' : 'MORE DETAIL'}
          <ChevronDown className={`w-3 h-3 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div className="px-5 md:px-6 pb-5 md:pb-6 pt-1 border-t border-white/5">
        <HeatBarDemo />
      </div>

      <div className="px-5 md:px-6 py-4 bg-white/[0.02] border-t border-white/5">
        <div className="flex flex-col md:flex-row flex-wrap items-start md:items-center gap-3 md:gap-x-6 md:gap-y-2 text-[11px] md:text-[10px] text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
            Cross-checked against AP, Google News & Reddit
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
            Updated every 6 hours
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0" />
            No editorial bias &mdash; algorithm only
          </span>
        </div>
      </div>
    </>
  )
}

export default function ScoringExplainer() {
  const [isOpen, setIsOpen] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [isOpen, isMobile])

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-[11px] md:text-[10px] font-semibold tracking-[0.1em] text-gray-500 active:text-white md:hover:text-white transition-colors group touch-target"
      >
        <Zap className="w-3.5 h-3.5 md:w-3 md:h-3 text-amber-500/70 group-hover:text-amber-400 transition-colors" />
        HOW WE RANK
        <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && isMobile && (
        <>
          <div className="bottom-sheet-backdrop" onClick={() => setIsOpen(false)} />
          <div className="bottom-sheet">
            <div className="bottom-sheet-handle" />
            <ExplainerContent showDetails={showDetails} setShowDetails={setShowDetails} onClose={() => setIsOpen(false)} />
          </div>
        </>
      )}

      {isOpen && !isMobile && (
        <div className="mt-6 rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.03] to-transparent overflow-hidden">
          <ExplainerContent showDetails={showDetails} setShowDetails={setShowDetails} onClose={() => setIsOpen(false)} />
        </div>
      )}
    </div>
  )
}
